import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { arrayRemove, arrayUnion, doc, getDoc, updateDoc } from 'firebase/firestore';

import { firestore } from '../firebase';
import { displayNameOf, userSchema } from '../../shared/schemas';
import { useAppState } from '../lib/appState';
import { usePunFeed } from '../lib/usePunFeed';
import PunCard from './PunCard';

/** Another player's puns, with a follow button. */
function PlayerPuns() {
  const { userId: routeUserId } = useParams();
  const { userId: viewerId } = useAppState();

  const [name, setName] = useState('');
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = routeUserId === viewerId;

  const { puns, names, loading, crownSpent, react } = usePunFeed({
    scope: 'user',
    viewerId,
    targetUserId: routeUserId,
    range: { kind: 'all' },
  });

  useEffect(() => {
    if (!routeUserId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [theirDoc, myDoc] = await Promise.all([
          getDoc(doc(firestore, 'users', routeUserId)),
          isSelf ? Promise.resolve(null) : getDoc(doc(firestore, 'users', viewerId)),
        ]);

        if (cancelled) return;

        if (theirDoc.exists()) {
          const parsed = userSchema.safeParse({ ...theirDoc.data(), id: theirDoc.id });
          if (parsed.success) setName(displayNameOf(parsed.data));
        }

        if (myDoc?.exists()) {
          const parsed = userSchema.safeParse({ ...myDoc.data(), id: myDoc.id });
          if (parsed.success) setFollowing(parsed.data.friends.includes(routeUserId));
        }
      } catch (err) {
        console.error('[PlayerPuns] could not load profile:', err);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [routeUserId, viewerId, isSelf]);

  const toggleFollow = useCallback(async () => {
    if (!routeUserId || isSelf) return;

    setBusy(true);
    setError(null);
    const next = !following;

    try {
      // arrayUnion/arrayRemove rather than rewriting the list, so a concurrent
      // change elsewhere is not clobbered.
      await updateDoc(doc(firestore, 'users', viewerId), {
        friends: next ? arrayUnion(routeUserId) : arrayRemove(routeUserId),
      });
      setFollowing(next);
    } catch (err) {
      console.error('[PlayerPuns] follow toggle failed:', err);
      setError(next ? 'Could not follow them.' : 'Could not unfollow them.');
    } finally {
      setBusy(false);
    }
  }, [routeUserId, viewerId, isSelf, following]);

  if (!routeUserId) {
    return <p className="py-16 text-center font-display text-xl font-bold">No such player.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h1 className="min-w-0 truncate font-display text-2xl font-bold">
          {name || 'Loading...'}
        </h1>

        {!isSelf && (
          <button
            onClick={toggleFollow}
            disabled={busy}
            className={`press shrink-0 rounded-full border-2 border-ink px-5 py-2 font-display text-sm font-bold shadow-pop-sm disabled:opacity-50 ${
              following ? 'bg-white' : 'bg-mustard'
            }`}
          >
            {busy ? '...' : following ? 'Following' : 'Follow'}
          </button>
        )}
      </div>

      {error && (
        <p className="mt-4 rounded-xl border-2 border-ink bg-coral px-4 py-3 text-sm font-medium text-white shadow-pop-sm">
          {error}
        </p>
      )}

      <div className="mt-7">
        {loading ? (
          <div className="flex justify-center py-12">
            <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
          </div>
        ) : puns.length === 0 ? (
          <p className="rounded-2xl border-2 border-dashed border-ink/30 bg-white/60 px-6 py-10 text-center text-sm text-ink/60">
            No puns from them yet.
          </p>
        ) : (
          <div className="space-y-5">
            {puns.map((pun) => (
              <PunCard
                key={pun.id}
                pun={pun}
                authorName={names[pun.userId] ?? name}
                viewerId={viewerId}
                crownSpent={crownSpent}
                onReact={(reaction) => react(pun, reaction)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayerPuns;
